import React, { useState } from 'react';
import { X, MessageSquare, Users } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { apiPost } from '../../utils/api';
import UserSearchSelect from './UserSearchSelect';

export default function NewConversationModal({ onClose, onConversationCreated }) {
    const { theme } = useTheme();
    const [mode, setMode] = useState(null); // null, 'direct', 'group'
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [groupTitle, setGroupTitle] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleCreate = async () => {
        if (selectedUsers.length === 0) return;

        setLoading(true);
        setError('');

        try {
            let body;
            if (mode === 'direct') {
                body = {
                    conversation_type: 'direct',
                    user_id: selectedUsers[0].id
                };
            } else {
                if (!groupTitle.trim()) {
                    setError('Group name is required');
                    setLoading(false);
                    return;
                }
                body = {
                    conversation_type: 'group',
                    title: groupTitle.trim(),
                    user_ids: selectedUsers.map(u => u.id)
                };
            }

            const response = await apiPost('/conversations', body);
            const data = await response.json();

            if (response.ok) {
                onConversationCreated(data.conversation);
                onClose();
            } else {
                setError(data.error || 'Failed to create conversation');
            }
        } catch (err) {
            setError('Failed to create conversation');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className={`w-full max-w-md rounded-xl shadow-xl ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}>
                {/* Header */}
                <div className={`flex items-center justify-between px-5 py-4 border-b ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    <h2 className="text-lg font-dm-serif font-semibold">
                        {!mode ? 'New Conversation' : mode === 'direct' ? 'New Direct Message' : 'New Group Chat'}
                    </h2>
                    <button onClick={onClose} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-5">
                    {/* Mode selection */}
                    {!mode && (
                        <div className="space-y-3">
                            <button
                                onClick={() => setMode('direct')}
                                className={`flex items-center gap-3 w-full p-4 rounded-lg border transition-colors ${
                                    theme === 'dark'
                                        ? 'border-gray-600 hover:bg-gray-700'
                                        : 'border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <MessageSquare size={24} className="text-blue-500" />
                                <div className="text-left">
                                    <p className="font-unna font-medium">Direct Message</p>
                                    <p className={`text-sm font-rasa ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                    }`}>Start a 1-on-1 conversation</p>
                                </div>
                            </button>
                            <button
                                onClick={() => setMode('group')}
                                className={`flex items-center gap-3 w-full p-4 rounded-lg border transition-colors ${
                                    theme === 'dark'
                                        ? 'border-gray-600 hover:bg-gray-700'
                                        : 'border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <Users size={24} className="text-purple-500" />
                                <div className="text-left">
                                    <p className="font-unna font-medium">Group Chat</p>
                                    <p className={`text-sm font-rasa ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                    }`}>Create a group with multiple members</p>
                                </div>
                            </button>
                        </div>
                    )}

                    {/* Group title input */}
                    {mode === 'group' && (
                        <div className="mb-4">
                            <label className={`block text-sm font-rasa font-medium mb-1 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>Group Name</label>
                            <input
                                type="text"
                                value={groupTitle}
                                onChange={(e) => setGroupTitle(e.target.value)}
                                placeholder="Enter group name..."
                                className={`w-full px-3 py-2 rounded-lg text-sm font-rasa border ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            />
                        </div>
                    )}

                    {/* User selection */}
                    {mode && (
                        <div>
                            <label className={`block text-sm font-rasa font-medium mb-1 ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                {mode === 'direct' ? 'Select user' : 'Add members'}
                            </label>
                            <UserSearchSelect
                                selectedUsers={selectedUsers}
                                context={mode === 'direct' ? 'dm' : 'group'}
                                onSelect={(user) => {
                                    if (mode === 'direct') {
                                        setSelectedUsers([user]);
                                    } else {
                                        setSelectedUsers(prev => [...prev, user]);
                                    }
                                }}
                                onRemove={(userId) => {
                                    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
                                }}
                            />
                        </div>
                    )}

                    {error && (
                        <p className="mt-3 text-sm font-rasa text-red-500">{error}</p>
                    )}

                    {/* Actions */}
                    {mode && (
                        <div className="flex justify-end gap-2 mt-5">
                            <button
                                onClick={() => {
                                    setMode(null);
                                    setSelectedUsers([]);
                                    setGroupTitle('');
                                    setError('');
                                }}
                                className={`px-4 py-2 rounded-lg text-sm font-rasa ${
                                    theme === 'dark' ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                                }`}
                            >
                                Back
                            </button>
                            <button
                                onClick={handleCreate}
                                disabled={loading || selectedUsers.length === 0}
                                className="px-4 py-2 rounded-lg text-sm font-rasa bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Creating...' : 'Create'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
